import React, { useState, useEffect, useRef  } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import Button from '../../components/ui/Button';
import InputWithIcon from '../../components/ui/InputWithIcon';
import OrderNewItem from '../order/OrderNewItem';   // 👈 import your component

const AddOrderPage = ({ setView, setEditingContext }) => {
  const { user } = useAuth();
  const customerInputRef = useRef(null);
  const createOrderBtnRef = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [editableAddress, setEditableAddress] = useState('');
  const [refNo, setRefNo] = useState('');
  const [amount, setAmount] = useState('');
  const [payMode, setPayMode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const gdId = sessionStorage.getItem("gdid");

  // ✅ New state to trigger Add Item modal in OrderItems
  const [openAddItemModal, setOpenAddItemModal] = useState(false);
  const [editingContextLocal, setEditingContextLocal] = useState(null);

  // Focus on customer input when page loads
  useEffect(() => {
    if (customerInputRef.current) customerInputRef.current.focus();
  }, []);

  useEffect(() => {
    api.getCustomerListWithBalance(user.UsrName)
      .then(data => {
        setCustomers(data.Table || []);
        setPaymentModes(data.Table1 || []);
        if (data.Table1?.length > 0) setPayMode(data.Table1[0].Mode);
      })
      .catch(() => setError('Failed to fetch customers.'))
      .finally(() => setLoading(false));
  }, [user.UsrName]);

  useEffect(() => {
    if (customerSearch) {
      const results = customers.filter(c =>
        c.Vname.toLowerCase().includes(customerSearch.toLowerCase()) ||
        c.Mobile.includes(customerSearch)
      );
      const exactMatch =
        selectedCustomer &&
        selectedCustomer.Vname.toLowerCase() === customerSearch.toLowerCase();
      setFilteredCustomers(exactMatch ? [] : results);
      setIsDropdownOpen(!exactMatch && results.length > 0);
    } else {
      setFilteredCustomers(customers);
      setIsDropdownOpen(customers.length > 0);
    }
    setHighlightIndex(-1);
  }, [customerSearch, customers, selectedCustomer]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.Vname);
    setEditableAddress(`${customer.Address1}, ${customer.City}`);
    setIsDropdownOpen(false);
    setFilteredCustomers([]);
    setHighlightIndex(-1);

    // Focus Create Order button after selection
    setTimeout(() => {
      if (createOrderBtnRef.current) createOrderBtnRef.current.focus();
    }, 50);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredCustomers[highlightIndex]) {
        handleCustomerSelect(filteredCustomers[highlightIndex]);
      } else if (filteredCustomers.length === 1) {
        handleCustomerSelect(filteredCustomers[0]);
      } else if (selectedCustomer) {
        handleCreateOrder();
      }
      return;
    }

    if (!isDropdownOpen || filteredCustomers.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex(prev =>
        prev < filteredCustomers.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex(prev =>
        prev > 0 ? prev - 1 : filteredCustomers.length - 1
      );
    }
  };

  const handleCreateOrder = async () => {
    if (!selectedCustomer) {
      setError("Please select a customer.");
      return;
    }

    const amountAsNumber = parseFloat(amount);
    if (amount && (isNaN(amountAsNumber) || amountAsNumber < 0)) {
      setError('Please enter a valid amount.');
      return;
    }

    if (!gdId) {
      setError('Godown not selected. Please select a godown first.');
      return;
    }

    setError("");
    setLoading(true);

    try {
      const createResult = await api.createOrder({
        User: user.UsrName,
        Vid: selectedCustomer.Vid,
        VName: selectedCustomer.Vname,
        Address: editableAddress,
        Phone: selectedCustomer.Mobile,
        OrderDate: orderDate,
        SessionId: "9944010155",
        amountPaid: amount ? amountAsNumber : 0,
        Paymode: amount ? payMode : '',
        Refno: amount ? refNo : '',
        GdId: Number(gdId)
      });

      if (createResult?.Table?.length > 0) {
        const newOrderId = createResult.Table[0].POId;
        const selectedOrderData = await api.getSelectedOrder(newOrderId);

        const ctx = {
          id: newOrderId,
          customerId: selectedCustomer.Vid,
          orderDate,
          customer: selectedCustomer,
          orderDetails: selectedOrderData
        };

        setEditingContext(ctx);
        setEditingContextLocal(ctx);   // 👈 save for OrderNewItem
        setSuccess(`Order created successfully with ID: ${newOrderId}`);

        // ✅ Trigger navigation and Add Item modal
        setTimeout(() => {
          setView('new-order');      
          setOpenAddItemModal(true);  
        }, 500);
      } else {
        setError("Failed to create order.");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="p-5 bg-white shadow-md border-b">
        <h2 className="text-2xl font-bold text-center text-gray-800 tracking-tight">
          📝 Create New Order
        </h2>
      </div>

      {/* Main Card */}
      <div className="flex justify-center mt-8">
        <div className="w-full max-w-xl p-6 bg-white shadow-lg rounded-2xl">
          
          {/* Order Date */}
          <div className="mb-6">
            <label className="block mb-2 text-sm font-semibold text-gray-700">
              📅 Delivery Date*
            </label>
            <input
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          {/* Customer Search */}
          <div className="relative mb-6">
            <InputWithIcon
              label="Search & Select Customer*"
              value={customerSearch}
              ref={customerInputRef}
              onChange={(e) => setCustomerSearch(e.target.value)}
              onFocus={() => setIsDropdownOpen(true)}
              onKeyDown={handleKeyDown}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              }
            />

            {isDropdownOpen && filteredCustomers.length > 0 && (
              <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto divide-y divide-gray-100">
                {filteredCustomers.map((c, idx) => (
                  <li
                    key={c.Vid}
                    onMouseDown={() => handleCustomerSelect(c)}
                    className={`flex flex-col px-3 py-2 cursor-pointer transition-all duration-150 ${
                      idx === highlightIndex
                        ? "bg-blue-100 text-blue-700 font-semibold"
                        : "hover:bg-blue-50 hover:text-blue-600"
                    } rounded-lg`}
                  >
                    <span className="font-medium text-gray-800">{c.Vname}</span>
                    <span className="text-xs text-gray-500">{c.Mobile}</span>
                  </li>
                ))}
              </ul>
            )}

          </div>

        {/* Customer Details */}
        <div className="p-5 mb-6 bg-gray-50 border rounded-xl shadow-sm">
          <h3 className="flex items-center gap-2 pb-2 mb-4 text-lg font-semibold text-gray-800 border-b">
            👤 Customer Details
          </h3>

          <label className="block mb-2 text-sm font-semibold text-gray-700">
            Delivery Address*
          </label>
          <textarea
            value={editableAddress}
            onChange={(e) => setEditableAddress(e.target.value)}
            rows="3"
            className="w-full p-3 mb-4 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter delivery address"
          />

          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium text-gray-700">📞 Mobile:</span>{" "}
              {selectedCustomer ? selectedCustomer.Mobile : "---"}
            </p>
            <p>
              <span className="font-medium text-gray-700">💰 Current Balance:</span>
              <span
                className={`ml-1 font-bold ${
                  selectedCustomer
                    ? selectedCustomer.Balance <= 0
                      ? "text-green-600"
                      : "text-red-600"
                    : "text-gray-400"
                }`}
              >
                {selectedCustomer
                  ? selectedCustomer.Balance.toLocaleString("en-IN", {
                      style: "currency",
                      currency: "INR",
                    })
                  : "₹0.00"}
              </span>
            </p>
          </div>

          {/* Payment Section */}
          <div className="mt-6">
            <h4 className="flex items-center gap-2 pb-2 mb-4 text-base font-semibold text-gray-800 border-b">
              💳 Payment (Optional)
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <InputWithIcon
                label="Ref No"
                value={refNo}
                onChange={(e) => setRefNo(e.target.value)}
              />
              <InputWithIcon
                label="Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Pay Mode
                </label>
                <select
                  value={payMode}
                  onChange={(e) => setPayMode(e.target.value)}
                  className="w-full p-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {paymentModes.map((mode, idx) => (
                    <option key={idx} value={mode.Mode}>
                      {mode.Mode}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-3 justify-end">
            <Button
              ref={createOrderBtnRef}
              onClick={handleCreateOrder}
              disabled={!selectedCustomer || loading}
              className="px-5 py-2 text-sm font-semibold"
            >
              {loading ? "Creating..." : "✅ Create Order"}
            </Button>
            <Button
              onClick={() => setView("orders")}
              className="px-5 py-2 text-sm font-semibold bg-gray-500 hover:bg-gray-600"
            >
              ❌ Cancel
            </Button>
          </div>
      </div>
    </div>

    {/* ✅ Mount OrderNewItem here */}
      {editingContextLocal && (
        <OrderNewItem
          setView={setView}
          context={editingContextLocal}
          user={user}
          setSuccess={setSuccess}
          setError={setError}
          openModal={openAddItemModal}         // ✅ pass state
          setOpenModal={setOpenAddItemModal}   // ✅ pass setter
        />
      )}
  </div>
  );
};

export default AddOrderPage;






// import React, { useState, useEffect } from 'react';
// import { useAuth } from '../../context/AuthContext';
// import api from '../../api/api';
// import Button from '../../components/ui/Button';
// import InputWithIcon from '../../components/ui/InputWithIcon';

// const AddOrderPage = ({ setView, setEditingContext }) => {
//   const { user } = useAuth();

//   const [customers, setCustomers] = useState([]);
//   const [paymentModes, setPaymentModes] = useState([]);
//   const [customerSearch, setCustomerSearch] = useState('');
//   const [filteredCustomers, setFilteredCustomers] = useState([]);
//   const [selectedCustomer, setSelectedCustomer] = useState(null);
//   const [isDropdownOpen, setIsDropdownOpen] = useState(false);
//   const [highlightIndex, setHighlightIndex] = useState(-1); // ✅ highlight index for arrow navigation

//   const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
//   const [editableAddress, setEditableAddress] = useState('');
//   const [refNo, setRefNo] = useState('');
//   const [amount, setAmount] = useState('');
//   const [payMode, setPayMode] = useState('');
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState('');

//   // Get GdId from sessionStorage
//   const gdId = sessionStorage.getItem("gdid");

//   useEffect(() => {
//     api.getCustomerListWithBalance(user.UsrName)
//       .then(data => {
//         setCustomers(data.Table || []);
//         setPaymentModes(data.Table1 || []);
//         if (data.Table1?.length > 0) {
//           setPayMode(data.Table1[0].Mode);
//         }
//       })
//       .catch(() => setError('Failed to fetch customers.'))
//       .finally(() => setLoading(false));
//   }, [user.UsrName]);

//   useEffect(() => {
//     if (customerSearch) {
//       const results = customers.filter(c =>
//         c.Vname.toLowerCase().includes(customerSearch.toLowerCase()) ||
//         c.Mobile.includes(customerSearch)
//       );
//       const exactMatch = selectedCustomer && selectedCustomer.Vname.toLowerCase() === customerSearch.toLowerCase();
//       setFilteredCustomers(exactMatch ? [] : results);
//       setIsDropdownOpen(!exactMatch && results.length > 0);
//       setHighlightIndex(-1); // reset highlight when search changes
//     } else {
//       setFilteredCustomers([]);
//       setIsDropdownOpen(false);
//       setHighlightIndex(-1);
//     }
//   }, [customerSearch, customers, selectedCustomer]);

//   const handleCustomerSelect = (customer) => {
//     setSelectedCustomer(customer);
//     setCustomerSearch(customer.Vname);
//     setEditableAddress(`${customer.Address1}, ${customer.City}`);
//     setIsDropdownOpen(false);
//     setFilteredCustomers([]);
//     setHighlightIndex(-1);
//   };

//   // ✅ Handle keyboard navigation
//   const handleKeyDown = (e) => {
//     if (!isDropdownOpen || filteredCustomers.length === 0) return;

//     if (e.key === "ArrowDown") {
//       e.preventDefault();
//       setHighlightIndex((prev) =>
//         prev < filteredCustomers.length - 1 ? prev + 1 : 0
//       );
//     } else if (e.key === "ArrowUp") {
//       e.preventDefault();
//       setHighlightIndex((prev) =>
//         prev > 0 ? prev - 1 : filteredCustomers.length - 1
//       );
//     } else if (e.key === "Enter") {
//       e.preventDefault();
//       if (highlightIndex >= 0 && filteredCustomers[highlightIndex]) {
//         handleCustomerSelect(filteredCustomers[highlightIndex]);
//       } else if (filteredCustomers.length === 1) {
//         // Auto select if only one match
//         handleCustomerSelect(filteredCustomers[0]);
//       }
//     }
//   };

//   const handleCreateOrder = async () => {
//     if (!selectedCustomer) {
//       setError("Please select a customer.");
//       return;
//     }

//     const amountAsNumber = parseFloat(amount);
//     if (amount && (isNaN(amountAsNumber) || amountAsNumber < 0)) {
//       setError('Please enter a valid amount.');
//       return;
//     }

//     if (!gdId) {
//       setError('Godown not selected. Please select a godown first.');
//       return;
//     }

//     setError("");
//     setLoading(true);

//     try {
//       const createResult = await api.createOrder({
//         User: user.UsrName,
//         Vid: selectedCustomer.Vid,
//         VName: selectedCustomer.Vname,
//         Address: editableAddress,
//         Phone: selectedCustomer.Mobile,
//         OrderDate: orderDate,
//         SessionId: "9944010155",
//         amountPaid: amount ? amountAsNumber : 0,
//         Paymode: amount ? payMode : '',
//         Refno: amount ? refNo : '',
//         GdId: Number(gdId) // ✅ include selected godown
//       });

//       if (createResult?.Table?.length > 0) {
//         const newOrderId = createResult.Table[0].POId;
//         const selectedOrderData = await api.getSelectedOrder(newOrderId);

//         setEditingContext({
//           id: newOrderId,
//           customerId: selectedCustomer.Vid,
//           orderDate,
//           customer: selectedCustomer,
//           orderDetails: selectedOrderData
//         });

//         setSuccess(`Order created successfully with ID: ${newOrderId}`);
//         setTimeout(() => setView('new-order'), 1200);
//       } else {
//         setError("Failed to create order.");
//       }
//     } catch (err) {
//       console.error(err);
//       setError("An error occurred. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//   <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
//     {/* Header */}
//     <div className="p-5 bg-white shadow-md border-b">
//       <h2 className="text-2xl font-bold text-center text-gray-800 tracking-tight">
//         📝 Create New Order
//       </h2>
//     </div>

//     {/* Main Card */}
//     <div className="flex justify-center mt-8">
//       <div className="w-full max-w-xl p-6 bg-white shadow-lg rounded-2xl">
        
//          {/* Order Date */}
//         <div className="mb-6">
//           <label
//             htmlFor="orderDate"
//             className="block mb-2 text-sm font-semibold text-gray-700"
//           >
//             📅 Delivery Date*
//           </label>
//           <input
//             type="date"
//             id="orderDate"
//             value={orderDate}
//             onChange={(e) => setOrderDate(e.target.value)}
//             className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//             required
//           />
//         </div>

//         {/* Customer Search */}
//         <div className="relative mb-6">
//           <InputWithIcon
//             label="Search & Select Customer*"
//             value={customerSearch}
//             onChange={(e) => setCustomerSearch(e.target.value)}
//             onFocus={() => setIsDropdownOpen(true)}
//             onKeyDown={handleKeyDown} // ✅ enable keyboard nav
//             icon={
//               <svg xmlns="http://www.w3.org/2000/svg"
//                 className="w-5 h-5 text-gray-400"
//                 fill="none"
//                 viewBox="0 0 24 24"
//                 stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
//                   d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
//               </svg>
//             }
//           />
//           {isDropdownOpen && filteredCustomers.length > 0 && (
//             <ul className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto divide-y">
//               {filteredCustomers.map((c, idx) => (
//                 <li
//                   key={c.Vid}
//                   onMouseDown={() => handleCustomerSelect(c)}
//                   className={`px-4 py-3 cursor-pointer transition-colors ${
//                     idx === highlightIndex
//                       ? "bg-blue-100 text-blue-700 font-semibold"
//                       : "hover:bg-blue-50"
//                   }`}
//                 >
//                   <p className="font-semibold text-gray-800">{c.Vname}</p>
//                   <p className="text-xs text-gray-500">{c.Mobile}</p>
//                 </li>
//               ))}
//             </ul>
//           )}
//         </div>

       

//         {/* Customer Details */}
//         {selectedCustomer && (
//           <div className="p-5 mb-6 bg-gray-50 border rounded-xl shadow-sm">
//             <h3 className="flex items-center gap-2 pb-2 mb-4 text-lg font-semibold text-gray-800 border-b">
//               👤 Customer Details
//             </h3>

//             <label className="block mb-2 text-sm font-semibold text-gray-700">
//               Delivery Address*
//             </label>
//             <textarea
//               value={editableAddress}
//               onChange={(e) => setEditableAddress(e.target.value)}
//               rows="3"
//               className="w-full p-3 mb-4 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />

//             <div className="space-y-2 text-sm">
//               <p>
//                 <span className="font-medium text-gray-700">📞 Mobile:</span>{" "}
//                 {selectedCustomer.Mobile}
//               </p>
//               <p>
//                 <span className="font-medium text-gray-700">💰 Current Balance:</span>
//                 <span
//                   className={`ml-1 font-bold ${
//                     selectedCustomer.Balance <= 0 ? "text-green-600" : "text-red-600"
//                   }`}
//                 >
//                   {selectedCustomer.Balance.toLocaleString("en-IN", {
//                     style: "currency",
//                     currency: "INR",
//                   })}
//                 </span>
//               </p>
//             </div>

//             {/* Payment Section */}
//             <div className="mt-6">
//               <h4 className="flex items-center gap-2 pb-2 mb-4 text-base font-semibold text-gray-800 border-b">
//                 💳 Payment (Optional)
//               </h4>
//               <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
//                 <InputWithIcon
//                   label="Ref No"
//                   value={refNo}
//                   onChange={(e) => setRefNo(e.target.value)}
//                 />
//                 <InputWithIcon
//                   label="Amount"
//                   type="number"
//                   value={amount}
//                   onChange={(e) => setAmount(e.target.value)}
//                 />
//                 <div>
//                   <label className="block mb-2 text-sm font-semibold text-gray-700">
//                     Pay Mode
//                   </label>
//                   <select
//                     value={payMode}
//                     onChange={(e) => setPayMode(e.target.value)}
//                     className="w-full p-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   >
//                     {paymentModes.map((mode, idx) => (
//                       <option key={idx} value={mode.Mode}>
//                         {mode.Mode}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Action Buttons */}
//         <div className="flex gap-3 pt-3 justify-end">
//           <Button
//             onClick={handleCreateOrder}
//             disabled={!selectedCustomer || loading}
//             className="px-5 py-2 text-sm font-semibold"
//           >
//             {loading ? "Creating..." : "✅ Create Order"}
//           </Button>
//           <Button
//             onClick={() => setView("orders")}
//             className="px-5 py-2 text-sm font-semibold bg-gray-500 hover:bg-gray-600"
//           >
//             ❌ Cancel
//           </Button>
//         </div>
//       </div>
//     </div>
//   </div>
// );

// };

// export default AddOrderPage;

